import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import Layout from './components/Layout';
import ErrorBoundary from './components/ErrorBoundary';
import Login from './pages/Login';

const Dashboard = lazy(() => import('./pages/Dashboard'));
const Customers = lazy(() => import('./pages/Customers'));
const Vouchers = lazy(() => import('./pages/Vouchers'));
const Ledger = lazy(() => import('./pages/Ledger'));
const Invoices = lazy(() => import('./pages/Invoices'));
const ExpenseDocuments = lazy(() => import('./pages/ExpenseDocuments'));
const TaxDeclaration = lazy(() => import('./pages/TaxDeclaration'));
const Payroll = lazy(() => import('./pages/Payroll'));
const TaxCalculator = lazy(() => import('./pages/TaxCalculator'));
const Bank = lazy(() => import('./pages/Bank'));
const StatementGenerator = lazy(() => import('./pages/StatementGenerator'));
const ShortLinks = lazy(() => import('./pages/ShortLinks'));
const Certificates = lazy(() => import('./pages/Certificates'));
const Inventory = lazy(() => import('./pages/Inventory'));
const FixedAssets = lazy(() => import('./pages/FixedAssets'));
const ReceivablesPayables = lazy(() => import('./pages/ReceivablesPayables'));
const FinancialStatements = lazy(() => import('./pages/FinancialStatements'));
const PeriodClose = lazy(() => import('./pages/PeriodClose'));
const Settings = lazy(() => import('./pages/Settings'));
const Users = lazy(() => import('./pages/Users'));
const NotFound = lazy(() => import('./pages/NotFound'));

function Protected({ children }: { children: JSX.Element }) {
  const { token, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted">加载中…</div>;
  if (!token) return <Navigate to="/login" replace />;
  return children;
}

function Fallback() {
  return <div className="p-6 text-sm text-muted">页面加载中…</div>;
}

export default function App() {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<Protected><Layout><Suspense fallback={<Fallback />}><Dashboard /></Suspense></Layout></Protected>} />
        <Route path="/customers" element={<Protected><Layout><Suspense fallback={<Fallback />}><Customers /></Suspense></Layout></Protected>} />
        <Route path="/vouchers/*" element={<Protected><Layout><Suspense fallback={<Fallback />}><Vouchers /></Suspense></Layout></Protected>} />
        <Route path="/ledger/*" element={<Protected><Layout><Suspense fallback={<Fallback />}><Ledger /></Suspense></Layout></Protected>} />
        <Route path="/invoices/*" element={<Protected><Layout><Suspense fallback={<Fallback />}><Invoices /></Suspense></Layout></Protected>} />
        <Route path="/expenses" element={<Protected><Layout><Suspense fallback={<Fallback />}><ExpenseDocuments /></Suspense></Layout></Protected>} />
        <Route path="/tax-declaration" element={<Protected><Layout><Suspense fallback={<Fallback />}><TaxDeclaration /></Suspense></Layout></Protected>} />
        <Route path="/payroll" element={<Protected><Layout><Suspense fallback={<Fallback />}><Payroll /></Suspense></Layout></Protected>} />
        <Route path="/tax-calculator" element={<Protected><Layout><Suspense fallback={<Fallback />}><TaxCalculator /></Suspense></Layout></Protected>} />
        <Route path="/bank" element={<Protected><Layout><Suspense fallback={<Fallback />}><Bank /></Suspense></Layout></Protected>} />
        <Route path="/statement" element={<Protected><Layout><Suspense fallback={<Fallback />}><StatementGenerator /></Suspense></Layout></Protected>} />
        <Route path="/short-links/*" element={<Protected><Layout><Suspense fallback={<Fallback />}><ShortLinks /></Suspense></Layout></Protected>} />
        <Route path="/certificates/*" element={<Protected><Layout><Suspense fallback={<Fallback />}><Certificates /></Suspense></Layout></Protected>} />
        <Route path="/inventory" element={<Protected><Layout><Suspense fallback={<Fallback />}><Inventory /></Suspense></Layout></Protected>} />
        <Route path="/fixed-assets/*" element={<Protected><Layout><Suspense fallback={<Fallback />}><FixedAssets /></Suspense></Layout></Protected>} />
        <Route path="/receivables-payables" element={<Protected><Layout><Suspense fallback={<Fallback />}><ReceivablesPayables /></Suspense></Layout></Protected>} />
        <Route path="/financial-statements/*" element={<Protected><Layout><Suspense fallback={<Fallback />}><FinancialStatements /></Suspense></Layout></Protected>} />
        <Route path="/period-close/*" element={<Protected><Layout><Suspense fallback={<Fallback />}><PeriodClose /></Suspense></Layout></Protected>} />
        <Route path="/settings/*" element={<Protected><Layout><Suspense fallback={<Fallback />}><Settings /></Suspense></Layout></Protected>} />
        <Route path="/users" element={<Protected><Layout><Suspense fallback={<Fallback />}><Users /></Suspense></Layout></Protected>} />
        <Route path="*" element={<Suspense fallback={<Fallback />}><NotFound /></Suspense>} />
        </Routes>
      </ErrorBoundary>
    </BrowserRouter>
  );
}
